import * as React from 'react'
import { styled } from '@mui/material/styles'
import { css } from '@emotion/react'

import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'

import { I18n, useTranslation } from 'components/hooks/use-translation'

import ResponsiveContainer from 'components/atomic/organisms/responsive-container'
import MarkdownViewer from 'components/atomic/organisms/markdown-viewer'
import Text from 'components/atomic/atoms/text'
import Chip from 'components/atomic/atoms/chip'

import { colorsFromItemType } from 'utils/paths'
import { Track, Path, PathGoal } from 'types/types'

const HeroWrapper = styled('div')`
    position: relative;
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};
`

const StyledContainer = styled(ResponsiveContainer)`
    padding-top: 4rem;
    padding-bottom: 4rem;
`

const BackgroundImage = styled('div')<{ type: PathGoal }>`
    background: url('/assets/lines2.png');
    background-repeat: no-repeat;
    background-position: right;
    background-size: cover;
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: -1;
    opacity: 0.7;

    ${({ type }) => {
        if (type === 'career') {
            return css`
                filter: invert(61%) sepia(4%) saturate(7137%) hue-rotate(314deg) brightness(91%) contrast(100%);
            `
        }

        if (type === 'skill') {
            return css`
                filter: invert(67%) sepia(17%) saturate(5877%) hue-rotate(190deg) brightness(95%) contrast(91%);
            `
        }

        if (type === 'course') {
            return css`
                filter: invert(69%) sepia(52%) saturate(5228%) hue-rotate(242deg) brightness(93%) contrast(95%);
            `
        }
    }}

    ${({ theme }) => theme.breakpoints.down('md')} {
        opacity: 0.2;
    }
`

interface HeroProps {
    item: Track | Path
    itemType: PathGoal
}

const Hero = ({ item, itemType }: HeroProps) => {
    const { t }: I18n = useTranslation()

    const colors = React.useMemo(() => colorsFromItemType(itemType), [itemType])

    return (
        <HeroWrapper>
            <BackgroundImage type={itemType} />
            <StyledContainer maxWidth="lg">
                <Grid item xs={12} sm={12} md>
                    <Stack spacing={5}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t(`common:pathGoal.${itemType}`)}
                                    sx={{ borderColor: colors[0], backgroundColor: colors[1] }}
                                />

                                {!item.live && <Chip variant="outlined" label={'Coming Soon'} size="small" />}

                                {/* eslint-disable-next-line @next/next/no-img-element  */}
                                <img
                                    alt="Italy flag"
                                    src={'/assets/flag-it.svg'}
                                    style={{ height: 20, width: 20, borderRadius: '50%' }}
                                />

                                {/* eslint-disable-next-line @next/next/no-img-element  */}
                                <img
                                    alt="Italy flag"
                                    src={'/assets/flag-gb.svg'}
                                    style={{ height: 20, width: 20, borderRadius: '50%' }}
                                />
                            </Stack>

                            {item?.categories.length && (
                                <Text variant="subtitle2" fontWeight="bold">
                                    {item.categories
                                        .slice(0, 4)
                                        .map((cat) => cat.title)
                                        .join(' · ')}
                                </Text>
                            )}
                        </Stack>

                        <Text variant="h2">{item.title}</Text>

                        <MarkdownViewer>{item.short_description}</MarkdownViewer>
                    </Stack>
                </Grid>
            </StyledContainer>
        </HeroWrapper>
    )
}

export default Hero
